<template>
  <teleport to="body">
    <transition name="toast-fade" @enter="onEnter" @leave="onLeave">
      <div v-if="isVisible" class="toast-notification" :class="`toast-${type}`" role="alert"
        :aria-live="type === 'error' ? 'assertive' : 'polite'">
        <div class="toast-inner">
          <div class="toast-icon-wrapper">
            <i :class="iconClass" aria-hidden="true"></i>
          </div>
          <div class="toast-content">
            <span class="toast-message">{{ internalMessage }}</span>
          </div>
          <button type="button" class="toast-close" @click="close" :aria-label="`Close ${type} notification`">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div v-if="showProgress" class="toast-progress" :style="{ animationDuration: `${duration}ms` }"></div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  props: {
    message: String,
    type: { type: String, default: "success" },
    duration: { type: Number, default: 3000 },
    cartItems: { type: Array, default: () => [] },
  },
  data() {
    return {
      isVisible: false,
      timeoutId: null,
      internalMessage: "",
      showProgress: true,
    };
  },
  computed: {
    iconClass() {
      return {
        success: "fas fa-check-circle",
        error: "fas fa-exclamation-circle",
        info: "fas fa-info-circle",
        warning: "fas fa-exclamation-triangle",
      }[this.type] || "fas fa-info-circle";
    },
  },
  methods: {
    show() {
      if (!this.message) return;
      this.internalMessage = this.message;
      this.isVisible = true;
      if (this.timeoutId) clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(this.close, this.duration);
    },
    close() {
      this.isVisible = false;
      this.internalMessage = "";
      if (this.timeoutId) clearTimeout(this.timeoutId);
      this.timeoutId = null;
      this.$emit("close");
    },
    onEnter() {
      // Animation hook for enter transition
    },
    onLeave() {
      // Animation hook for leave transition
    },
  },
  beforeUnmount() {
    if (this.timeoutId) clearTimeout(this.timeoutId);
  },
};
</script>

<style scoped>
.toast-notification {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 10000;
  min-width: 320px;
  max-width: 480px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.12);
  background: white;
  animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-inner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
}

.toast-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  font-size: 20px;
}

.toast-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toast-message {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  word-break: break-word;
}

.toast-close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0 -8px 0 0;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  opacity: 0.7;
}

.toast-close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.05);
}

.toast-close:active {
  transform: scale(0.95);
}

.toast-close svg {
  width: 20px;
  height: 20px;
}

.toast-progress {
  height: 3px;
  width: 100%;
  animation: progress linear forwards;
  transform-origin: left;
}

/* Toast Type Styles */
.toast-success {
  border-left: 4px solid #10b981;
}

.toast-success .toast-icon-wrapper {
  color: #10b981;
}

.toast-success .toast-message {
  color: #1f2937;
}

.toast-success .toast-close {
  color: #6b7280;
}

.toast-success .toast-progress {
  background: #10b981;
}

.toast-error {
  border-left: 4px solid #ef4444;
}

.toast-error .toast-icon-wrapper {
  color: #ef4444;
}

.toast-error .toast-message {
  color: #1f2937;
}

.toast-error .toast-close {
  color: #6b7280;
}

.toast-error .toast-progress {
  background: #ef4444;
}

.toast-warning {
  border-left: 4px solid #f59e0b;
}

.toast-warning .toast-icon-wrapper {
  color: #f59e0b;
}

.toast-warning .toast-message {
  color: #1f2937;
}

.toast-warning .toast-close {
  color: #6b7280;
}

.toast-warning .toast-progress {
  background: #f59e0b;
}

.toast-info {
  border-left: 4px solid #3b82f6;
}

.toast-info .toast-icon-wrapper {
  color: #3b82f6;
}

.toast-info .toast-message {
  color: #1f2937;
}

.toast-info .toast-close {
  color: #6b7280;
}

.toast-info .toast-progress {
  background: #3b82f6;
}

/* Animations */
@keyframes slideInRight {
  from {
    transform: translateX(400px);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes progress {
  from {
    width: 100%;
  }

  to {
    width: 0%;
  }
}

.toast-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-fade-enter-from {
  transform: translateX(400px);
  opacity: 0;
}

.toast-fade-leave-to {
  transform: translateX(400px);
  opacity: 0;
}

/* Mobile Responsiveness */
@media (max-width: 640px) {
  .toast-notification {
    left: 12px;
    right: 12px;
    min-width: auto;
    max-width: none;
    top: 12px;
  }

  .toast-inner {
    padding: 14px 16px;
    gap: 12px;
  }

  .toast-message {
    font-size: 13px;
  }

  .toast-close {
    width: 28px;
    height: 28px;
  }
}
</style>
